/**
 * SharingApi over fetch (contracts routes: page sharing, public links, file sharing, sharing settings) with the
 * session's access token: refreshed before it expires, and once more after a 401. `publicPage` sends no token (the
 * /s/:token page of signed-out visitors).
 */
import type {
  FileSharingResponse,
  InvitePageGuestResponse,
  Message,
  PageSharingResponse,
  PublicPageResponse,
  SharingSettingsResponse,
} from "@bundle/contracts";
import { buildPath, routes, type RouteName } from "@bundle/contracts/routes";
import { ApiFailure, type ApiErrorKind, type SessionViewModel, type SharingApi } from "@bundle/core";
import { API_BASE } from "../env.ts";

function kindFor(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 429) return "rateLimited";
  if (status >= 400 && status < 500) return "invalid";
  return status === 502 || status === 503 || status === 504 ? "network" : "server";
}

export function createSharingApi(session: SessionViewModel | undefined, base: string = API_BASE): SharingApi {
  async function call<T>(name: RouteName, params: Record<string, string>, body?: unknown, opts: { query?: Record<string, string | undefined>; auth?: boolean; idempotencyKey?: string } = {}): Promise<T> {
    const route = routes[name];
    let url = base + buildPath(route.path, params);
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined) q.set(k, v);
    if (q.toString() !== "") url += `?${q.toString()}`;
    const auth = opts.auth !== false && session !== undefined;
    const send = async (token: string | undefined) => {
      const headers: Record<string, string> = { accept: "application/json" };
      if (body !== undefined) headers["content-type"] = "application/json";
      if (token !== undefined) headers.authorization = `Bearer ${token}`;
      if (opts.idempotencyKey !== undefined) headers["idempotency-key"] = opts.idempotencyKey;
      try {
        return await fetch(url, { method: route.method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
      } catch (e) {
        throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
      }
    };
    let token: string | undefined;
    if (auth) {
      await session.ensureFreshToken();
      token = session.state.session?.token;
    }
    let res = await send(token);
    if (auth && res.status === 401 && token !== undefined && (await session.authRejected(token))) {
      token = session.state.session?.token;
      res = await send(token);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let json: unknown;
    try {
      json = text === "" ? undefined : JSON.parse(text);
    } catch {
      json = undefined;
    }
    if (!res.ok) {
      const err = (json as { error?: { code?: string; message?: string } } | undefined)?.error;
      throw new ApiFailure(kindFor(res.status), res.status, err?.code ?? String(res.status), err?.message ?? res.statusText);
    }
    return json as T;
  }

  const sharing = async (p: Promise<PageSharingResponse>) => (await p).sharing;
  const fileSharing = async (p: Promise<FileSharingResponse>) => (await p).sharing;
  return {
    pageSharing: (ws, pageId) => sharing(call("pageSharing", { ws, pageId })),
    setPermission: (ws, pageId, userId, access) => sharing(call("setPagePermission", { ws, pageId, userId }, { access })),
    removePermission: (ws, pageId, userId) => sharing(call("removePagePermission", { ws, pageId, userId })),
    setGeneralAccess: (ws, pageId, general) => sharing(call("setGeneralAccess", { ws, pageId }, { general })),
    inviteGuest: (ws, pageId, req) => call<InvitePageGuestResponse>("invitePageGuest", { ws, pageId }, req),
    publish: (ws, pageId, req) => sharing(call("publishPage", { ws, pageId }, req)),
    unpublish: (ws, pageId) => sharing(call("unpublishPage", { ws, pageId })),
    fileSharing: (ws, fileId) => fileSharing(call("fileSharing", { ws, fileId })),
    createFileLink: (ws, fileId, req) => fileSharing(call("createFileLink", { ws, fileId }, req)),
    revokeFileLink: (ws, fileId) => fileSharing(call("revokeFileLink", { ws, fileId })),
    shareFile: async (ws, fileId, req) => (await call<{ message: Message }>("shareFile", { ws, fileId }, req, req.clientId === undefined ? {} : { idempotencyKey: req.clientId })).message,
    settings: (ws) => call<SharingSettingsResponse>("sharingSettings", { ws }),
    updateSettings: (ws, req) => call<SharingSettingsResponse>("updateSharingSettings", { ws }, req),
    publicPage: (token, pageId) => call<PublicPageResponse>("publicPage", { token }, undefined, { query: { page: pageId }, auth: false }),
  };
}
